import React, { useMemo } from 'react';
import { Checkbox, Select, Row, Col, Button, Space, Typography, theme } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { DEPENDENCY_SOURCE_TYPES, DEPENDENCY_OPERATORS, DEFAULT_CONDITION } from '../utils/constants';
import DebouncedInput from './DebouncedInput';

const { Text } = Typography;

/**
 * Collects all questions across the entire schema, excluding a specific ID.
 */
const collectAllQuestions = (schema, excludeId) => {
  if (!schema?.pages) return [];
  return schema.pages.flatMap((page, pi) =>
    (page.sections || []).flatMap((section, si) =>
      (section.questions || [])
        .filter(q => q.id !== excludeId)
        .map(q => ({
          id: q.id,
          label: q.label || q.id,
          pageLabel: page.label,
          sectionLabel: section.label,
          answers: q.questionOptions?.answers?.filter(a => a.type !== 'separator' && a.type !== 'linebreak') || [],
          pageIndex: pi,
          sectionIndex: si,
        }))
    )
  );
};

/**
 * Collects all sections across the schema with unique keys.
 */
const collectAllSections = (schema) => {
  if (!schema?.pages) return [];
  return schema.pages.flatMap((page, pi) =>
    (page.sections || []).map((section, si) => ({
      key: `${pi}:${si}`,
      label: section.label,
      pageLabel: page.label,
    }))
  );
};

/**
 * Renders a single condition row with source-type-aware controls.
 */
const ConditionRow = ({ condition, index, allQuestions, allSections, onChange, onRemove }) => {
  const sourceType = condition.sourceType || 'question';

  const updateField = (field, value) => {
    const updated = { ...condition, [field]: value };
    // Reset dependent fields when source type changes
    if (field === 'sourceType') {
      updated.question = '';
      updated.answerRef = '';
      updated.sectionRef = '';
      updated.answerCoding = '';
      updated.operator = value === 'section' ? 'exists' : '=';
    }
    // Reset answerRef when question changes for answer source
    if (field === 'question' && sourceType === 'answer') {
      updated.answerRef = '';
    }
    onChange(index, updated);
  };

  // Get answers for the selected question (for answer source type)
  const selectedQuestion = allQuestions.find(q => q.id === condition.question);
  const questionAnswers = selectedQuestion?.answers || [];

  return (
    <Row gutter={[6, 6]} align="middle" style={{ marginBottom: 6 }}>
      {/* Source type */}
      <Col span={5}>
        <Select
          size="small"
          value={sourceType}
          onChange={(val) => updateField('sourceType', val)}
          style={{ width: '100%' }}
        >
          {DEPENDENCY_SOURCE_TYPES.map(t => (
            <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
          ))}
        </Select>
      </Col>

      {/* Source selector: question/answer → question dropdown; section → section dropdown */}
      {(sourceType === 'question' || sourceType === 'answer') && (
        <Col span={sourceType === 'answer' ? 5 : 7}>
          <Select
            size="small"
            placeholder="Question"
            value={condition.question || undefined}
            onChange={(val) => updateField('question', val)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
          >
            {allQuestions.map(q => (
              <Select.Option key={q.id} value={q.id}>
                {q.label} <Text type="secondary" style={{ fontSize: 10 }}>({q.sectionLabel})</Text>
              </Select.Option>
            ))}
          </Select>
        </Col>
      )}

      {/* Answer selector (only for answer source type) */}
      {sourceType === 'answer' && (
        <Col span={5}>
          <Select
            size="small"
            placeholder="Answer"
            value={condition.answerRef || undefined}
            onChange={(val) => updateField('answerRef', val)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
            disabled={!condition.question}
          >
            {questionAnswers.map((a, ai) => {
              const val = a.concept || a.conceptCode || `answer_${ai}`;
              return (
                <Select.Option key={val} value={val}>{a.label || val}</Select.Option>
              );
            })}
          </Select>
        </Col>
      )}

      {/* Section selector */}
      {sourceType === 'section' && (
        <Col span={7}>
          <Select
            size="small"
            placeholder="Section"
            value={condition.sectionRef || undefined}
            onChange={(val) => updateField('sectionRef', val)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
          >
            {allSections.map(s => (
              <Select.Option key={s.key} value={s.key}>
                {s.label} <Text type="secondary" style={{ fontSize: 10 }}>({s.pageLabel})</Text>
              </Select.Option>
            ))}
          </Select>
        </Col>
      )}

      {/* Operator */}
      <Col span={4}>
        <Select
          size="small"
          value={condition.operator || '='}
          onChange={(val) => updateField('operator', val)}
          style={{ width: '100%' }}
        >
          {DEPENDENCY_OPERATORS.map(op => (
            <Select.Option key={op.value} value={op.value}>{op.label}</Select.Option>
          ))}
        </Select>
      </Col>

      {/* Value (hidden for exists/!exists and section source) */}
      {!['exists', '!exists'].includes(condition.operator) && sourceType !== 'section' && sourceType !== 'answer' && (
        <Col span={5}>
          <DebouncedInput
            size="small"
            placeholder="Value"
            value={condition.answerCoding || ''}
            onChange={(e) => updateField('answerCoding', e.target.value)}
          />
        </Col>
      )}

      {/* Delete */}
      <Col span={2}>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
      </Col>
    </Row>
  );
};

/**
 * Universal Dependency Editor — reusable across sections, questions, and answers.
 * Manages enableWhen conditions array and enableBehavior.
 *
 * @param {Array}    conditions     - Current enableWhen conditions
 * @param {string}   behavior       - 'any' | 'all'
 * @param {Function} onUpdate       - (enableWhen, enableBehavior) => void
 * @param {Object}   schema         - Full form schema
 * @param {string}   excludeQuestionId - Question ID to exclude from source options
 * @param {string}   label          - Custom label for the checkbox
 */
const DependencyEditor = ({
  conditions = [],
  behavior = 'any',
  onUpdate,
  schema,
  excludeQuestionId,
  label = 'Conditional Display',
}) => {
  const { token } = theme.useToken();
  const hasConditions = conditions.length > 0;

  const allQuestions = useMemo(() => collectAllQuestions(schema, excludeQuestionId), [schema, excludeQuestionId]);
  const allSections = useMemo(() => collectAllSections(schema), [schema]);

  const handleToggle = (checked) => {
    if (checked) {
      onUpdate([{ ...DEFAULT_CONDITION }], 'any');
    } else {
      onUpdate([], null);
    }
  };

  const handleConditionChange = (index, updated) => {
    const next = [...conditions];
    next[index] = updated;
    onUpdate(next, behavior);
  };

  const handleRemove = (index) => {
    const next = conditions.filter((_, i) => i !== index);
    onUpdate(next, next.length > 0 ? behavior : null);
  };

  const handleAdd = () => {
    onUpdate([...conditions, { ...DEFAULT_CONDITION }], behavior);
  };

  const handleBehaviorChange = (val) => {
    onUpdate(conditions, val);
  };

  return (
    <div style={{ padding: 8, backgroundColor: token.colorFillTertiary, borderRadius: 4 }}>
      <Checkbox checked={hasConditions} onChange={(e) => handleToggle(e.target.checked)}>
        <Text strong style={{ fontSize: 12 }}>{label}</Text>
      </Checkbox>

      {hasConditions && (
        <div style={{ marginTop: 10, paddingLeft: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {conditions.map((cond, i) => (
              <ConditionRow
                key={i}
                condition={cond}
                index={i}
                allQuestions={allQuestions}
                allSections={allSections}
                onChange={handleConditionChange}
                onRemove={handleRemove}
              />
            ))}
            <Row gutter={8}>
              <Col span={12}>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={handleAdd} block>
                  Add Condition
                </Button>
              </Col>
              <Col span={12}>
                <Select
                  size="small"
                  value={behavior || 'any'}
                  onChange={handleBehaviorChange}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="any">Any (OR)</Select.Option>
                  <Select.Option value="all">All (AND)</Select.Option>
                </Select>
              </Col>
            </Row>
          </Space>
        </div>
      )}
    </div>
  );
};

export default DependencyEditor;

